import { useState, useEffect, useRef } from 'react';
import { GPTCheckboxItem } from '../types';

interface UseGPTCheckboxManagerProps {
    currentItems: GPTCheckboxItem[];
    aiSuggestions: GPTCheckboxItem[];
    onChange: (items: GPTCheckboxItem[]) => void;
}

export const useGPTCheckboxManager = ({
    currentItems,
    aiSuggestions,
    onChange,
}: UseGPTCheckboxManagerProps) => {
    const [allItems, setAllItems] = useState<GPTCheckboxItem[]>([]);
    const prevAiSuggestionsRef = useRef<GPTCheckboxItem[]>([]);

    useEffect(() => {
        const aiSuggestionsChanged
            = aiSuggestions.length !== prevAiSuggestionsRef.current.length
            || aiSuggestions.some((item, i) => item.id !== prevAiSuggestionsRef.current[i]?.id);

        if (aiSuggestionsChanged) {
            prevAiSuggestionsRef.current = aiSuggestions;

            const existingItemsMap = new Map(allItems.map(item => [item.id, item]));

            const updatedAiItems = aiSuggestions.map(aiItem => {
                const existing = existingItemsMap.get(aiItem.id);
                if (existing) {
                    return existing;
                }
                return aiItem;
            });

            const customItems = allItems.filter(item => !item.isGenerated);

            const newAllItems = [...updatedAiItems, ...customItems];
            setAllItems(newAllItems);

            onChange(newAllItems.filter(item => item.isChecked));
        }
    }, [aiSuggestions, onChange]);

    useEffect(() => {
        const customFormDataItems = currentItems.filter(item => !item.isGenerated);
        const existingCustomIds = new Set(allItems.filter(item => !item.isGenerated).map(item => item.id));

        const newCustomItems = customFormDataItems.filter(item => !existingCustomIds.has(item.id));
        if (newCustomItems.length > 0) {
            setAllItems(prev => [...prev, ...newCustomItems]);
        }
    }, [currentItems]);

    const handleCheckboxCheck = (index: number, checked: boolean) => {
        const updatedItems = [...allItems];
        updatedItems[index] = { ...updatedItems[index], isChecked: checked };
        setAllItems(updatedItems);
        onChange(updatedItems.filter(item => item.isChecked));
    };

    const handleAddCustomItem = (name: string, description: string) => {
        const newItem: GPTCheckboxItem = {
            id: crypto.randomUUID(),
            name,
            description,
            isChecked: true,
            isGenerated: false,
        };
        const updatedItems = [...allItems, newItem];
        setAllItems(updatedItems);
        onChange(updatedItems.filter(item => item.isChecked));
    };

    const handleDeleteCheckbox = (id: string) => {
        const updatedItems = allItems.filter(item => item.id !== id);
        setAllItems(updatedItems);
        onChange(updatedItems.filter(item => item.isChecked));
    };

    const handleEditCheckbox = (id: string, name: string, description: string) => {
        const updatedItems = allItems.map(item => (item.id === id ? { ...item, name, description } : item));
        setAllItems(updatedItems);
        onChange(updatedItems.filter(item => item.isChecked));
    };

    return {
        allItems,
        handleCheckboxCheck,
        handleAddCustomItem,
        handleDeleteCheckbox,
        handleEditCheckbox,
    };
};
